import { ClockIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";

import { Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

const LoadingBar: React.FC<{ progress: number }> = ({ progress }) => {
  return (
    <div className="w-full max-w-md bg-gray-200 h-1 rounded-full overflow-hidden relative">
      <div
        className="absolute top-0 left-0 h-full bg-green-500 rounded-full transition-all duration-300 ease-out"
        style={{ width: `${progress}%`, minWidth: progress > 0 ? "2px" : "0px" }}
      />
    </div>
  );
};

export interface ProgressScreenProps {
  /** Main title displayed above the progress bar */
  title?: string;
  /** Subtitle/description displayed below the title */
  subtitle?: string;
  /** Custom icon component to display */
  icon?: React.ReactNode;
  /** Duration in seconds for the progress bar to reach 100% (default: 5) */
  duration?: number;
  /** Maximum timeout in seconds before forcing completion (default: 7) */
  maxTimeout?: number;
  /** Whether the external process has completed */
  isCompleted?: boolean;
  /** Callback fired when progress reaches 100% or timeout occurs */
  onComplete?: () => void;
  /** Callback fired when the component starts (useful for triggering workflows) */
  onStart?: () => void;
  /** Additional CSS classes for the container */
  className?: string;
  /** Height of the container (default: "h-96") */
  height?: string;
}

export const ProgressScreen: React.FC<ProgressScreenProps> = ({
  title = "Processing...",
  subtitle = "Please wait while we complete this operation.",
  icon,
  duration = 5,
  maxTimeout = 7,
  isCompleted = false,
  onComplete,
  onStart,
  className = "",
  height = "h-96",
}) => {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const maxTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCompletedRef = useRef(false);
  const hasStartedRef = useRef(false);

  // Trigger onStart callback once
  useEffect(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      onStart?.();
    }
  }, [onStart]);

  // Progress bar logic
  useEffect(() => {
    // Don't start if already completed
    if (isCompletedRef.current) {
      return;
    }

    // Initialize start time only once when component mounts
    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now();
      setProgress(0);
      isCompletedRef.current = false;
    }

    // Immediately update progress once to ensure it starts
    const elapsed = startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : 0;
    const initialProgress = Math.min((elapsed / duration) * 100, 100);
    if (initialProgress > 0) {
      setProgress(initialProgress);
    }

    const updateProgress = () => {
      if (isCompletedRef.current || startTimeRef.current === null) return;

      const elapsedTime = (Date.now() - startTimeRef.current) / 1000;

      if (isCompleted && !isCompletedRef.current) {
        // External process completed - match progress to actual time
        const actualProgress = Math.min((elapsedTime / duration) * 100, 100);
        setProgress(actualProgress);
        isCompletedRef.current = true;

        // Clear intervals
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        if (maxTimeoutRef.current) {
          clearTimeout(maxTimeoutRef.current);
          maxTimeoutRef.current = null;
        }

        // Trigger completion callback
        requestAnimationFrame(() => {
          onComplete?.();
        });
        return;
      }

      if (elapsedTime >= maxTimeout) {
        // Max timeout reached - force transition
        setProgress(100);
        isCompletedRef.current = true;

        // Clear intervals
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        if (maxTimeoutRef.current) {
          clearTimeout(maxTimeoutRef.current);
          maxTimeoutRef.current = null;
        }

        onComplete?.();
        return;
      }

      // Update progress: 0-100% over duration seconds
      const targetProgress = Math.min((elapsedTime / duration) * 100, 100);
      setProgress(targetProgress);
    };

    // Only set up intervals if they don't already exist
    if (!progressIntervalRef.current) {
      progressIntervalRef.current = setInterval(updateProgress, 50);
    }

    // Only set timeout if it doesn't already exist
    if (!maxTimeoutRef.current) {
      maxTimeoutRef.current = setTimeout(() => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setProgress(100);
        isCompletedRef.current = true;
        onComplete?.();
      }, maxTimeout * 1000);
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
        maxTimeoutRef.current = null;
      }
    };
  }, [isCompleted, duration, maxTimeout, onComplete]);

  const defaultIcon = <ClockIcon className="h-8 w-8 text-gray-500" />;

  return (
    <div className={`flex w-full flex-col bg-limestone-50 ${height} items-center justify-center gap-y-4 ${className}`}>
      {icon ?? defaultIcon}
      <Headline size="extra-small" colorType="primary">
        {title}
      </Headline>
      <Paragraph displayType="normal" colorType="secondary">
        {subtitle}
      </Paragraph>
      <div className="w-full max-w-md mt-5">
        <LoadingBar progress={progress} />
      </div>
      <div className="flex max-w-max px-6 mt-16 py-2 bg-limestone-100 rounded-lg">
        <Paragraph colorType="primary" size="small">
          Always check our AI generated suggestions for bias, inaccuracies or mistakes.
        </Paragraph>
      </div>
    </div>
  );
};
